/**
 * Floating support widget. Mounted in Layout.js, visible on every page for
 * any authenticated internal user. Provides quick access to "my tickets" and a
 * "new ticket" modal. URS-F194.
 */
import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Lifebuoy, Plus, X, Paperclip, ChatCircleDots, ArrowLineUpRight } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { errText } from '../../utils/errors';
import { useNavigate } from 'react-router-dom';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import { useAuth } from '../../contexts/AuthContext';
import { usePermissions } from '../../contexts/PermissionsContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PRIORITY_COLORS = {
  baja: 'bg-stone-50 text-stone-600 border-stone-200',
  media: 'bg-blue-50 text-blue-700 border-blue-200',
  alta: 'bg-amber-50 text-amber-700 border-amber-200',
  critica: 'bg-red-50 text-red-700 border-red-200',
};
const STATUS_COLORS = {
  abierto: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  en_curso: 'bg-blue-50 text-blue-700 border-blue-200',
  resuelto: 'bg-stone-100 text-stone-600 border-stone-200',
  cerrado: 'bg-stone-100 text-stone-400 border-stone-200',
};

export default function SupportFloat({ lang = 'es' }) {
  const { user } = useAuth();
  const { can } = usePermissions();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [view, setView] = useState('list'); // list | new | detail
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [active, setActive] = useState(null); // ticket detail

  // new ticket form state
  const [form, setForm] = useState({ title: '', description: '', priority: 'media', screenshots: [] });
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // detail comment state
  const [newComment, setNewComment] = useState('');
  const [posting, setPosting] = useState(false);

  const isAuthed = !!user && user !== false;
  const canAccess = isAuthed && can('support', 'view');

  const loadList = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API_URL}/api/support/tickets?mine=true`, { withCredentials: true });
      setTickets(data || []);
    } catch { /* ignore */ }
    setLoading(false);
  }, []);

  useEffect(() => {
    if (open && view === 'list') loadList();
  }, [open, view, loadList]);

  const loadDetail = async (ticketId) => {
    try {
      const { data } = await axios.get(`${API_URL}/api/support/tickets/${ticketId}`, { withCredentials: true });
      setActive(data);
      setView('detail');
    } catch {
      toast.error(lang === 'es' ? 'No se pudo cargar el ticket' : 'Failed to load ticket');
    }
  };

  const handleFile = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    for (const file of files) {
      if (file.size > 5 * 1024 * 1024) {
        toast.error((lang === 'es' ? 'Maximo 5 MB: ' : 'Max 5 MB: ') + file.name);
        continue;
      }
      if (!['image/png', 'image/jpeg', 'image/webp', 'image/gif'].includes(file.type)) {
        toast.error((lang === 'es' ? 'Solo PNG/JPG/WebP: ' : 'PNG/JPG/WebP only: ') + file.name);
        continue;
      }
      const fd = new FormData();
      fd.append('file', file);
      try {
        const { data } = await axios.post(`${API_URL}/api/files/upload`, fd, { withCredentials: true });
        setForm(f => ({ ...f, screenshots: [...f.screenshots, { file_id: data.id, name: file.name }] }));
      } catch {
        toast.error((lang === 'es' ? 'Error subiendo ' : 'Upload failed ') + file.name);
      }
    }
    setUploading(false);
    e.target.value = '';
  };

  const submitTicket = async () => {
    if (!form.title.trim() || !form.description.trim()) {
      toast.error(lang === 'es' ? 'Titulo y descripcion son obligatorios' : 'Title and description are required');
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        title: form.title.trim(),
        description: form.description.trim(),
        priority: form.priority,
        screenshot_file_ids: form.screenshots.map(s => s.file_id),
        current_url: window.location.pathname,
        user_agent: navigator.userAgent,
      };
      await axios.post(`${API_URL}/api/support/tickets`, payload, { withCredentials: true });
      toast.success(lang === 'es' ? 'Ticket enviado a soporte' : 'Ticket sent to support');
      setForm({ title: '', description: '', priority: 'media', screenshots: [] });
      setView('list');
      await loadList();
    } catch (err) {
      toast.error(errText(err));
    }
    setSubmitting(false);
  };

  const postComment = async () => {
    if (!newComment.trim() || !active) return;
    setPosting(true);
    try {
      await axios.post(`${API_URL}/api/support/tickets/${active.ticket_id}/comments`,
        { text: newComment.trim() }, { withCredentials: true });
      setNewComment('');
      await loadDetail(active.ticket_id);
    } catch { toast.error('Error'); }
    setPosting(false);
  };

  if (!canAccess) return null;

  return (
    <>
      <button
        type="button"
        onClick={() => { setOpen(true); setView('list'); }}
        data-testid="support-float-btn"
        title={lang === 'es' ? 'Soporte' : 'Support'}
        className="fixed bottom-6 right-6 z-40 h-12 w-12 rounded-full bg-[#1f3e80] text-white shadow-lg hover:bg-[#2a4f8a] flex items-center justify-center transition-transform hover:scale-105"
      >
        <Lifebuoy size={22} weight="duotone" />
      </button>

      <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setView('list'); setActive(null); } }}>
        <DialogContent className="max-w-2xl max-h-[85vh] flex flex-col p-0" data-testid="support-dialog">
          {view === 'list' && (
            <>
              <DialogHeader className="px-5 pt-5 pb-3 border-b border-stone-100">
                <div className="flex items-center justify-between">
                  <DialogTitle className="flex items-center gap-2 text-[#1f3e80]">
                    <Lifebuoy size={20} weight="duotone" />
                    {lang === 'es' ? 'Mis tickets de soporte' : 'My support tickets'}
                  </DialogTitle>
                  <div className="flex items-center gap-2">
                    {can('support', 'edit') && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => { setOpen(false); navigate('/support'); }}
                        data-testid="support-open-admin"
                        className="text-xs gap-1"
                      >
                        <ArrowLineUpRight size={12} />
                        {lang === 'es' ? 'Vista soporte' : 'Support view'}
                      </Button>
                    )}
                    <Button
                      data-testid="support-new-btn"
                      onClick={() => setView('new')}
                      className="bg-[#1f3e80] hover:bg-[#2a4f8a] text-white gap-1 rounded-sm"
                      size="sm"
                    >
                      <Plus size={14} />
                      {lang === 'es' ? 'Nuevo ticket' : 'New ticket'}
                    </Button>
                  </div>
                </div>
              </DialogHeader>
              <div className="px-5 pb-5 overflow-y-auto flex-1">
                {loading ? (
                  <p className="text-sm text-stone-400 py-6 text-center">{lang === 'es' ? 'Cargando…' : 'Loading…'}</p>
                ) : tickets.length === 0 ? (
                  <div className="py-10 text-center">
                    <Lifebuoy size={32} className="mx-auto text-stone-300 mb-2" />
                    <p className="text-sm text-stone-500">{lang === 'es' ? 'Sin tickets. Si encuentras un bug o se te ocurre una mejora, abre uno desde aqui.' : 'No tickets yet. Open one if you spot a bug or have an improvement idea.'}</p>
                  </div>
                ) : (
                  <ul className="divide-y divide-stone-100">
                    {tickets.map(t => (
                      <li key={t.ticket_id} className="py-3 cursor-pointer hover:bg-stone-50 px-2 -mx-2 rounded-sm" onClick={() => loadDetail(t.ticket_id)} data-testid={`support-ticket-row-${t.ticket_id}`}>
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2 mb-0.5">
                              <span className="text-xs font-mono text-stone-400">{t.ticket_id}</span>
                              <Badge variant="outline" className={`text-[10px] ${STATUS_COLORS[t.status] || ''}`}>{t.status.replace('_',' ')}</Badge>
                              <Badge variant="outline" className={`text-[10px] ${PRIORITY_COLORS[t.priority] || ''}`}>{t.priority}</Badge>
                              {t.comments_count > 0 && (
                                <span className="text-[10px] text-stone-400 flex items-center gap-0.5"><ChatCircleDots size={11} />{t.comments_count}</span>
                              )}
                            </div>
                            <p className="text-sm font-medium text-stone-800 truncate">{t.title}</p>
                            <p className="text-xs text-stone-400 truncate">{t.description}</p>
                          </div>
                          <span className="text-[10px] text-stone-400 whitespace-nowrap mt-0.5">
                            {new Date(t.created_at).toLocaleDateString(lang === 'es' ? 'es-ES' : 'en-US')}
                          </span>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </>
          )}

          {view === 'new' && (
            <>
              <DialogHeader className="px-5 pt-5 pb-3 border-b border-stone-100">
                <DialogTitle className="flex items-center gap-2 text-[#1f3e80]">
                  <Plus size={18} />{lang === 'es' ? 'Nuevo ticket' : 'New ticket'}
                </DialogTitle>
              </DialogHeader>
              <div className="px-5 py-4 overflow-y-auto flex-1 space-y-4">
                <p className="text-xs text-stone-500">{lang === 'es' ? 'Describe lo que ocurre o lo que te gustaria mejorar. Adjunta una captura si ayuda a entenderlo.' : 'Describe the issue or improvement. Attach a screenshot if it helps.'}</p>
                <div className="space-y-1.5">
                  <Label>{lang === 'es' ? 'Titulo' : 'Title'} <span className="text-red-500">*</span></Label>
                  <Input data-testid="support-title-input" value={form.title} maxLength={200}
                    onChange={(e) => setForm(f => ({ ...f, title: e.target.value }))}
                    placeholder={lang === 'es' ? 'Ej: Al guardar un voluntario muestra error 500' : 'E.g. Saving a volunteer shows error 500'} />
                </div>
                <div className="space-y-1.5">
                  <Label>{lang === 'es' ? 'Prioridad' : 'Priority'}</Label>
                  <Select value={form.priority} onValueChange={(v) => setForm(f => ({ ...f, priority: v }))}>
                    <SelectTrigger data-testid="support-priority-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="baja">{lang === 'es' ? 'Baja' : 'Low'}</SelectItem>
                      <SelectItem value="media">{lang === 'es' ? 'Media' : 'Medium'}</SelectItem>
                      <SelectItem value="alta">{lang === 'es' ? 'Alta' : 'High'}</SelectItem>
                      <SelectItem value="critica">{lang === 'es' ? 'Critica (bloqueo)' : 'Critical (blocker)'}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label>{lang === 'es' ? 'Descripcion' : 'Description'} <span className="text-red-500">*</span></Label>
                  <Textarea data-testid="support-description-input" rows={6} maxLength={5000}
                    value={form.description}
                    onChange={(e) => setForm(f => ({ ...f, description: e.target.value }))}
                    placeholder={lang === 'es' ? 'Pasos para reproducirlo, lo que esperabas, lo que ocurrio…' : 'Steps to reproduce, expected vs actual…'} />
                </div>
                <div className="space-y-1.5">
                  <Label>{lang === 'es' ? 'Capturas de pantalla (opcional)' : 'Screenshots (optional)'}</Label>
                  <div className="flex items-center gap-2">
                    <input type="file" accept="image/png,image/jpeg,image/webp,image/gif" multiple onChange={handleFile} className="hidden" id="support-file-input" data-testid="support-screenshot-input" />
                    <label htmlFor="support-file-input" className="inline-flex items-center gap-1.5 px-3 py-2 rounded-sm border border-stone-200 bg-white text-sm text-stone-700 cursor-pointer hover:border-[#1f3e80]">
                      <Paperclip size={14} />
                      {uploading ? '...' : (lang === 'es' ? 'Adjuntar imágenes' : 'Attach images')}
                    </label>
                    {form.screenshots.length > 0 && (
                      <span className="text-xs text-stone-500">{form.screenshots.length} {lang === 'es' ? 'adjunta(s)' : 'attached'}</span>
                    )}
                  </div>
                  {form.screenshots.length > 0 && (
                    <div className="flex flex-wrap gap-2 pt-1" data-testid="support-screenshot-thumbs">
                      {form.screenshots.map((s, idx) => (
                        <div key={s.file_id} className="relative group" data-testid={`support-screenshot-thumb-${idx}`}>
                          <img src={`${API_URL}/api/files/download/${s.file_id}`} alt={s.name}
                            className="h-16 w-16 object-cover rounded-sm border border-stone-200" />
                          <button type="button" title={lang === 'es' ? 'Quitar' : 'Remove'}
                            data-testid={`support-screenshot-remove-${idx}`}
                            onClick={() => setForm(f => ({ ...f, screenshots: f.screenshots.filter((_, i) => i !== idx) }))}
                            className="absolute -top-1.5 -right-1.5 bg-white rounded-full border border-stone-200 text-red-400 hover:text-red-600 shadow-sm">
                            <X size={13} />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                  <p className="text-[11px] text-stone-400">PNG/JPG/WebP · max 5 MB {lang === 'es' ? 'por imagen' : 'each'}</p>
                </div>
                <div className="bg-stone-50 border border-stone-100 rounded-sm p-2 text-[11px] text-stone-500">
                  <p>{lang === 'es' ? `Pagina actual: ${window.location.pathname}` : `Current page: ${window.location.pathname}`}</p>
                </div>
              </div>
              <DialogFooter className="px-5 py-3 border-t border-stone-100">
                <Button variant="ghost" onClick={() => setView('list')} disabled={submitting}>{lang === 'es' ? 'Cancelar' : 'Cancel'}</Button>
                <Button onClick={submitTicket} disabled={submitting || !form.title.trim() || !form.description.trim()}
                  className="bg-[#1f3e80] hover:bg-[#2a4f8a] text-white rounded-sm gap-1" data-testid="support-submit-btn">
                  <Plus size={14} />{submitting ? '...' : (lang === 'es' ? 'Enviar ticket' : 'Send ticket')}
                </Button>
              </DialogFooter>
            </>
          )}

          {view === 'detail' && active && (
            <>
              <DialogHeader className="px-5 pt-5 pb-3 border-b border-stone-100">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <DialogTitle className="text-base text-stone-800 truncate">{active.title}</DialogTitle>
                    <div className="flex items-center gap-2 mt-1">
                      <span className="text-xs font-mono text-stone-400">{active.ticket_id}</span>
                      <Badge variant="outline" className={`text-[10px] ${STATUS_COLORS[active.status] || ''}`}>{active.status.replace('_',' ')}</Badge>
                      <Badge variant="outline" className={`text-[10px] ${PRIORITY_COLORS[active.priority] || ''}`}>{active.priority}</Badge>
                    </div>
                  </div>
                  <Button variant="ghost" size="sm" onClick={() => { setActive(null); setView('list'); }} className="text-xs">
                    {lang === 'es' ? '← Volver' : '← Back'}
                  </Button>
                </div>
              </DialogHeader>
              <div className="px-5 py-4 overflow-y-auto flex-1 space-y-4">
                <div className="bg-stone-50 border border-stone-100 rounded-sm p-3 text-sm whitespace-pre-wrap text-stone-700">{active.description}</div>
                {(() => {
                  const imgs = (active.screenshot_file_ids && active.screenshot_file_ids.length)
                    ? active.screenshot_file_ids
                    : (active.screenshot_file_id ? [active.screenshot_file_id] : []);
                  if (!imgs.length) return null;
                  return (
                    <div className="flex flex-wrap gap-2" data-testid="support-detail-images">
                      {imgs.map((fid, i) => (
                        <a key={fid} href={`${API_URL}/api/files/download/${fid}`} target="_blank" rel="noreferrer" className="block">
                          <img src={`${API_URL}/api/files/download/${fid}`} alt={`screenshot-${i}`} className="rounded-sm border border-stone-200 max-h-64 object-contain" />
                        </a>
                      ))}
                    </div>
                  );
                })()}
                {active.current_url && <p className="text-[11px] text-stone-400">{lang === 'es' ? 'Pagina:' : 'Page:'} <span className="font-mono">{active.current_url}</span></p>}

                <div className="space-y-2">
                  <p className="text-xs font-semibold text-stone-600 flex items-center gap-1"><ChatCircleDots size={14} />{lang === 'es' ? 'Conversacion' : 'Conversation'}</p>
                  {(active.comments || []).length === 0 ? (
                    <p className="text-xs text-stone-400 italic">{lang === 'es' ? 'Sin comentarios todavia.' : 'No comments yet.'}</p>
                  ) : (
                    <ul className="space-y-2">
                      {active.comments.map(c => (
                        <li key={c.comment_id} className={`p-2.5 rounded-sm text-sm border ${c.is_staff ? 'bg-blue-50 border-blue-100' : 'bg-stone-50 border-stone-100'}`}>
                          <div className="flex items-center gap-2 mb-1">
                            <span className={`text-xs font-semibold ${c.is_staff ? 'text-blue-700' : 'text-stone-700'}`}>{c.name}</span>
                            {c.is_staff && <Badge variant="outline" className="text-[10px] bg-blue-100 border-blue-200 text-blue-800">{lang === 'es' ? 'Soporte' : 'Support'}</Badge>}
                            <span className="text-[10px] text-stone-400 ml-auto">{new Date(c.timestamp).toLocaleString(lang === 'es' ? 'es-ES' : 'en-US')}</span>
                          </div>
                          <p className="whitespace-pre-wrap text-stone-700">{c.text}</p>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>

                {active.status !== 'cerrado' && (
                  <div className="space-y-2">
                    <Textarea data-testid="support-comment-input" rows={2} value={newComment}
                      onChange={(e) => setNewComment(e.target.value)}
                      placeholder={lang === 'es' ? 'Añade un comentario…' : 'Add a comment…'} />
                    <Button onClick={postComment} disabled={posting || !newComment.trim()}
                      className="bg-[#1f3e80] hover:bg-[#2a4f8a] text-white rounded-sm" size="sm" data-testid="support-comment-submit">
                      {posting ? '...' : (lang === 'es' ? 'Comentar' : 'Comment')}
                    </Button>
                  </div>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
